import { createContext, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  dashboardService, 
  type DashboardSummary,
  type ActiveUserGraph,
  type AccessLogGraph,
  type AccessLog,
  type DashboardCounts,
  type DashboardSummaryToday
} from '../../../shared/services/dashboard/dashboardService';

interface DashboardContextData {
  summary: DashboardSummary | null;
  activeUserGraph: ActiveUserGraph | null;
  accessLogGraph: AccessLogGraph | null;
  accessLogs: AccessLog[];
  dashboardCounts: DashboardCounts | null;
  dashboardSummaryToday: DashboardSummaryToday | null;
  isLoading: boolean;
  isLoadingActiveUserGraph: boolean;
  isLoadingAccessLogGraph: boolean;
  isLoadingAccessLogs: boolean;
  isLoadingDashboardCounts: boolean;
  isLoadingDashboardSummaryToday: boolean;
  error: string | null;
  refetch: () => void;
}

interface DashboardProviderProps {
  children: ReactNode;
}

export const DashboardContext = createContext<DashboardContextData>({} as DashboardContextData);

export function DashboardProvider({ children }: DashboardProviderProps) {
  const [error, setError] = useState<string | null>(null);

  const { data: summary, isLoading: isLoadingSummary, refetch: refetchSummary } = useQuery<DashboardSummary>({
    queryKey: ['dashboard-summary'],
    queryFn: async () => {
      try {
        return await dashboardService.getSummary();
      } catch (error: any) {
        setError(error.message || 'Erro ao carregar dados do dashboard');
        throw error;
      }
    }
  });

  const { data: activeUserGraph, isLoading: isLoadingActiveUserGraph } = useQuery<ActiveUserGraph>({
    queryKey: ['dashboard-active-users'],
    queryFn: async () => {
      try {
        return await dashboardService.getActiveUserGraph();
      } catch (error: any) {
        setError(error.message || 'Erro ao carregar gráfico de usuários ativos');
        throw error;
      }
    }
  });

  const { data: accessLogGraph, isLoading: isLoadingAccessLogGraph } = useQuery<AccessLogGraph>({
    queryKey: ['dashboard-access-logs-graph'],
    queryFn: async () => {
      try {
        return await dashboardService.getAccessLogGraph();
      } catch (error: any) {
        setError(error.message || 'Erro ao carregar gráfico de logs de acesso');
        throw error;
      }
    }
  });

  const { data: accessLogs = [], isLoading: isLoadingAccessLogs } = useQuery<AccessLog[]>({
    queryKey: ['dashboard-access-logs'],
    queryFn: async () => {
      try {
        return await dashboardService.getAccessLogs();
      } catch (error: any) {
        setError(error.message || 'Erro ao carregar logs de acesso');
        throw error;
      }
    }
  });

  const { data: dashboardCounts, isLoading: isLoadingDashboardCounts } = useQuery<DashboardCounts>({
    queryKey: ['dashboard-counts'],
    queryFn: async () => {
      try {
        return await dashboardService.getDashboardCounts();
      } catch (error: any) {
        setError(error.message || 'Erro ao carregar contagens do dashboard');
        throw error;
      }
    }
  });

  const { data: dashboardSummaryToday, isLoading: isLoadingDashboardSummaryToday } = useQuery<DashboardSummaryToday>({
    queryKey: ['dashboard-summary-today'],
    queryFn: async () => {
      try {
        return await dashboardService.getDashboardSummaryToday();
      } catch (error: any) {
        setError(error.message || 'Erro ao carregar resumo do dia');
        throw error;
      }
    }
  });

  const isLoading = isLoadingSummary || 
    isLoadingActiveUserGraph || 
    isLoadingAccessLogGraph || 
    isLoadingAccessLogs || 
    isLoadingDashboardCounts || 
    isLoadingDashboardSummaryToday;

  const refetch = () => {
    refetchSummary();
  };

  return (
    <DashboardContext.Provider
      value={{
        summary: summary ?? null,
        activeUserGraph: activeUserGraph ?? null,
        accessLogGraph: accessLogGraph ?? null,
        accessLogs,
        dashboardCounts: dashboardCounts ?? null,
        dashboardSummaryToday: dashboardSummaryToday ?? null,
        isLoading,
        isLoadingActiveUserGraph,
        isLoadingAccessLogGraph,
        isLoadingAccessLogs,
        isLoadingDashboardCounts,
        isLoadingDashboardSummaryToday,
        error,
        refetch
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
} 